'use client';

import { Bell } from '@phosphor-icons/react/ssr';
import { Slot } from 'radix-ui';
import {
  cloneElement,
  type ComponentProps,
  isValidElement,
  type ReactNode,
  useSyncExternalStore,
} from 'react';

import { cn } from '../../lib/utils';
import { Avatar } from './avatar';
import { IconButton, type IconButtonProps } from './icon-button';
import { Menu, MenuContent, MenuTrigger } from './menu';
import { tabStyles } from './tabs';
import { Tooltip } from './tooltip';

export type TopBarLayout = 'full' | 'with-sidebar';

export type TopBarProps = Omit<ComponentProps<'header'>, 'children'> & {
  /**
   * Figma "Layout". `full`: products without a sidebar - wordmark and the
   * top-level nav on the left. `with-sidebar`: the content-area header -
   * a Breadcrumb on the left and the page's primary action.
   */
  layout?: TopBarLayout;
  /** Left side: the wordmark + `TopBarNav`, or a Breadcrumb. Fills the space. */
  start?: ReactNode;
  /** The ⌘K `SearchField` (280 wide in Full, 320 With sidebar). */
  search?: ReactNode;
  /** With sidebar: the page's primary action (a Primary Small Button), 8px before the icons. */
  primaryAction?: ReactNode;
  /** Help Icon Button, `TopBarNotifications`, `TopBarAccount` - 4px apart. */
  actions?: ReactNode;
  /** Stick to the top of the scroller (Figma: "Sticky on scroll"). Default true. */
  sticky?: boolean;
  /**
   * Shadow/XS "once content scrolls beneath". Defaults to following the
   * window's scroll position; pass it when the page scrolls in a container.
   */
  elevated?: boolean;
};

const subscribe = (onChange: () => void) => {
  window.addEventListener('scroll', onChange, { passive: true });
  return () => window.removeEventListener('scroll', onChange);
};
const windowScrolled = () => window.scrollY > 0;

/**
 * Global header (Figma: Components ↳ Top Bar, 97:166): 56 tall, bg/surface
 * with a 1px border/subtle bottom stroke - Left (fills) · Search · Actions,
 * 24 apart. A `<header>`; sticky, adding Shadow/XS once content scrolls
 * beneath it. Keep one row and the same order everywhere.
 */
export function TopBar({
  layout = 'full',
  start,
  search,
  primaryAction,
  actions,
  sticky = true,
  elevated,
  className,
  ...props
}: TopBarProps) {
  const scrolled = useSyncExternalStore(subscribe, windowScrolled, () => false);
  const raised = elevated ?? (sticky && scrolled);
  return (
    <header
      data-slot="top-bar"
      data-layout={layout}
      data-elevated={raised || undefined}
      className={cn(
        'bg-bg-surface flex h-14 w-full shrink-0 items-center gap-6 pr-6',
        // Figma INSIDE bottom stroke (no layout space); Shadow/XS when raised.
        'shadow-[inset_0_-1px_0_0_var(--color-border-subtle)]',
        'data-elevated:shadow-[inset_0_-1px_0_0_var(--color-border-subtle),var(--shadow-xs)]',
        'motion-safe:transition-shadow motion-safe:duration-[120ms]',
        layout === 'full' ? 'pl-6' : 'pl-8',
        sticky && 'sticky top-0 z-40',
        className,
      )}
      {...props}
    >
      <div
        data-slot="top-bar-start"
        className={cn(
          'flex h-full min-w-0 flex-1 items-center',
          layout === 'full' ? 'gap-4' : 'gap-6',
        )}
      >
        {start}
      </div>
      {search}
      {(primaryAction || actions) && (
        <div data-slot="top-bar-actions" className="flex shrink-0 items-center gap-1">
          {primaryAction && (
            <>
              {primaryAction}
              <span aria-hidden className="w-2 shrink-0" />
            </>
          )}
          {actions}
        </div>
      )}
    </header>
  );
}

/**
 * Top-level navigation of the Full layout: Underline Tabs as links, 4 apart,
 * as tall as the bar (the indicator lands on its bottom stroke). A `<nav>`
 * landmark ("Primary" unless you pass `aria-label`).
 */
export function TopBarNav({
  className,
  children,
  'aria-label': ariaLabel = 'Primary',
  ...props
}: ComponentProps<'nav'>) {
  return (
    <nav
      aria-label={ariaLabel}
      data-slot="top-bar-nav"
      className={cn('h-full', className)}
      {...props}
    >
      <ul className="flex h-full items-center gap-1">{children}</ul>
    </nav>
  );
}

export type TopBarNavLinkProps = ComponentProps<'a'> & {
  /** The section you are in: `aria-current="page"`, brand/strong indicator. */
  active?: boolean;
  /** Render the single child (a router Link) with the tab styling. */
  asChild?: boolean;
};

/**
 * A Top Bar nav link, styled as the Figma Underline Tab (Label/Medium,
 * text/secondary → primary, 2px indicator border/strong on hover,
 * brand/strong when current) at the bar's 56 height, 1px up.
 */
export function TopBarNavLink({
  active = false,
  asChild = false,
  className,
  children,
  ...props
}: TopBarNavLinkProps) {
  const styles = tabStyles({ variant: 'underline' });
  const indicator = (
    <span aria-hidden data-slot="tab-indicator" className={styles.indicator()} />
  );
  const shared = {
    'data-slot': 'top-bar-nav-link',
    // The Tab styles key on Radix's data-state.
    'data-state': active ? 'active' : 'inactive',
    'aria-current': active ? ('page' as const) : undefined,
    className: styles.tab({ className: cn('-top-px h-14', className) }),
    ...props,
  };
  const child =
    asChild && isValidElement<{ children?: ReactNode }>(children) ? children : null;
  return (
    <li className="flex h-full items-center">
      {child ? (
        <Slot.Root {...shared}>
          {cloneElement(
            child,
            undefined,
            <>
              {child.props.children}
              {indicator}
            </>,
          )}
        </Slot.Root>
      ) : (
        <a {...shared}>
          {children}
          {indicator}
        </a>
      )}
    </li>
  );
}

export type TopBarNotificationsProps = Omit<IconButtonProps, 'icon' | 'aria-label'> & {
  /** Figma "Unread dot". Also added to the name ("Notifications, unread"). */
  unread?: boolean;
  'aria-label'?: string;
};

/**
 * Notifications: a Ghost Medium Icon Button (bell) with the 8px status/danger
 * unread dot (2px bg/surface ring) and a "Notifications" Tooltip. Wrap it in a
 * `PopoverTrigger asChild` to open the inbox.
 */
export function TopBarNotifications({
  unread = false,
  'aria-label': ariaLabel = 'Notifications',
  className,
  ...props
}: TopBarNotificationsProps) {
  return (
    <span data-slot="top-bar-notifications" className="relative inline-flex shrink-0">
      <Tooltip content={ariaLabel}>
        <IconButton
          icon={Bell}
          aria-label={unread ? `${ariaLabel}, unread` : ariaLabel}
          className={className}
          {...props}
        />
      </Tooltip>
      {unread && (
        <span
          aria-hidden
          data-slot="top-bar-unread-dot"
          className="bg-status-danger-solid ring-bg-surface pointer-events-none absolute top-[9px] left-6 size-2 rounded-full ring-2"
        />
      )}
    </span>
  );
}

export type TopBarAccountProps = {
  /** The signed-in user (names the button and the avatar initials). */
  name: string;
  src?: string;
  className?: string;
  /** Account Menu content. */
  children: ReactNode;
};

/**
 * Account: the 32px Avatar (in a 32×40 hit frame) as the Menu button,
 * "Account menu, Maya Kowalski"; the Menu opens below, end-aligned.
 */
export function TopBarAccount({ name, src, className, children }: TopBarAccountProps) {
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          data-slot="top-bar-account"
          aria-label={`Account menu, ${name}`}
          className={cn(
            'flex h-10 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none',
            // Focus: a 2px border/focus ring around the avatar (not drawn in Figma).
            'focus-visible:[&_[data-slot=avatar-base]]:ring-2 focus-visible:[&_[data-slot=avatar-base]]:ring-border-focus',
            className,
          )}
        >
          <Avatar size={32} name={name} src={src} alt="" />
        </button>
      </MenuTrigger>
      <MenuContent align="end">{children}</MenuContent>
    </Menu>
  );
}
